'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Modal / Dialog — Radix-backed, custom-styled.
 *
 * a11y:
 *   - Focus trap, ESC to close, scroll lock — all managed by Radix
 *   - Close button has accessible label
 *   - Background uses overlay token (teal-tinted)
 *
 * RTL: the close button positions via inline-end (logical), works in both dirs.
 */

export const Modal = DialogPrimitive.Root;
export const ModalTrigger = DialogPrimitive.Trigger;
export const ModalClose = DialogPrimitive.Close;

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showCloseButton?: boolean;
  }
>(({ className, children, size = 'md', showCloseButton = true, ...props }, ref) => {
  const sizeClassMap: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };
  const sizeClass = sizeClassMap[size];
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-40',
          'bg-surface-overlay backdrop-blur-xs',
          'data-[state=open]:animate-fade-in',
        )}
      />
      {/* R23 — Replaced the previous `left-1/2 + -translate-x-1/2`
          centering with a flex-centered wrapper. The translate approach
          was rendering off-center on Rafael's wide-viewport laptop
          (likely a CSS variable cascade quirk between Radix's portal
          and Tailwind's transform utilities). Flex centering on a
          fixed full-screen wrapper is bulletproof + viewport-relative,
          and pointer-events-none ensures clicks still hit the overlay. */}
      {/* Mobile (<640px): bottom-sheet idiom — dialog docks to the bottom
          edge, full width, dvh-capped so the iOS URL bar / keyboard can't
          hide the action row. Desktop keeps the centered card exactly
          as before (all mobile classes are max-sm:). */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none max-sm:items-end max-sm:p-0">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'relative w-full pointer-events-auto',
            sizeClass,
            // Cap height + internal scroll so tall dialogs (e.g. the
            // Create user modal with all its fields) stay fully visible.
            // dvh on mobile tracks the VISIBLE viewport (URL bar/keyboard).
            'max-h-[90vh] overflow-y-auto max-sm:max-h-[92dvh]',
            'bg-surface-elevated border border-border-subtle rounded-lg shadow-2xl',
            'max-sm:max-w-full max-sm:rounded-b-none',
            'focus:outline-none data-[state=open]:animate-fade-in-up',
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton ? (
            <DialogPrimitive.Close
              aria-label="Close dialog"
              className={cn(
                'absolute end-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md',
                'max-sm:h-11 max-sm:w-11 max-sm:end-1.5 max-sm:top-1.5',
                'text-text-muted hover:text-text-primary hover:bg-sand-100',
                'focus-visible:outline-none transition-colors duration-fast',
              )}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  );
});
ModalContent.displayName = DialogPrimitive.Content.displayName;

export const ModalHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 pt-6 pb-3 flex flex-col gap-1.5', className)} {...props} />
);

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-text-primary', className)}
    {...props}
  />
));
ModalTitle.displayName = DialogPrimitive.Title.displayName;

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-text-secondary', className)}
    {...props}
  />
));
ModalDescription.displayName = DialogPrimitive.Description.displayName;

export const ModalBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-4', className)} {...props} />
);

export const ModalFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'px-6 py-4 border-t border-border-subtle',
      'flex items-center justify-end gap-3',
      // Mobile: pin the action row inside the dialog's scroller so tall
      // dialogs never hide their buttons below the fold. Desktop unchanged.
      'max-sm:sticky max-sm:bottom-0 max-sm:bg-surface-elevated',
      className,
    )}
    {...props}
  />
);
